import { FxKind, HERO_LIST, HEROES, Slot } from "@rivalwatch/shared";
import { CLIENT_HEROES } from "../heroes/index.ts";
import { HERO_VOICE_LIST, type VoiceTrigger } from "../heroes/voices.ts";
import * as fb from "./cues/feedback.ts";
import * as loops from "./cues/loops.ts";
import * as mt from "./cues/match.ts";
import * as uiq from "./cues/ui.ts";
import * as wp from "./cues/weapons.ts";
import type { BusName, Cue, LoopVoice, Out } from "./engine.ts";
import { ANNOUNCER, type LineId } from "./lines.ts";
import { playTheme } from "./theory.ts";

/** Every sound in the game, grouped for the audio lab. */

export interface CatalogCue {
  id: string;
  group: string;
  name: string;
  cue: Cue;
  bus: BusName;
  /** Heard in the world (so the lab's position picker applies). */
  spatial?: boolean;
}

export interface CatalogLoop {
  id: string;
  group: string;
  name: string;
  build: (o: Out) => LoopVoice;
  bus: BusName;
  spatial?: boolean;
  /** Initial control value 0..1. */
  value?: number;
  /** What the control means, if it does anything. */
  control?: string;
}

export const GROUPS = ["Weapons", "Abilities", "Hero themes", "Explosions & impacts", "Handling", "Feedback", "Movement", "Match", "Interface", "Ambience"] as const;

export function cues(): CatalogCue[] {
  const out: CatalogCue[] = [];
  const add = (group: string, name: string, cue: Cue, bus: BusName = "sfx", spatial = bus === "sfx" || bus === "amb") => out.push({ id: `${group}/${name}`, group, name, cue, bus, spatial });

  for (const h of HERO_LIST) {
    const primary = h.abilities[Slot.Primary].name;
    add("Weapons", `${h.name} · ${primary}`, wp.fire(h.id));
    const alt = CLIENT_HEROES[h.id].audio.fire({ alt: true });
    if (alt.key !== CLIENT_HEROES[h.id].audio.fire().key) add("Weapons", `${h.name} · alternate fire`, alt.cue);
  }
  for (const h of HERO_LIST)
    for (const slot of [Slot.Secondary, Slot.Ability1, Slot.Ability2, Slot.Ultimate]) {
      const c = wp.ability(h.id, slot);
      const info = h.abilities[slot];
      if (c && info) add("Abilities", `${h.name} · ${info.name}`, c);
    }
  for (const h of HERO_LIST) {
    add("Hero themes", `${h.name} · heroic`, (o) => playTheme(o, h.id, "heroic"), "feedback");
    add("Hero themes", `${h.name} · hostile`, (o) => playTheme(o, h.id, "hostile"), "feedback");
    add("Hero themes", `${h.name} · tease`, (o) => playTheme(o, h.id, "tease"), "feedback");
  }

  const X = "Explosions & impacts";
  add(X, "Rocket", wp.explosion(FxKind.Rocket, 2.8));
  add(X, "Helix rocket", wp.explosion(FxKind.Rocket, 2));
  add(X, "Barrage missile", wp.explosion(FxKind.Missile, 1.8));
  add(X, "Concussive Blast", wp.explosion(FxKind.Concussion, 4));
  add(X, "Bio-Grenade", wp.explosion(FxKind.Spore, 4));
  add(X, "Maelstrom", wp.explosion(FxKind.Singularity, 6));
  add(X, "Soul Sever", wp.explosion(FxKind.Singularity, 12));
  add(X, "Hexblade on steel", wp.impact(FxKind.Pellet));
  add(X, "Repair Orb pop", wp.impact(FxKind.Spore));
  add(X, "Snare Trap snaps", wp.impact(FxKind.Hook));
  add(X, "Gateway teleport", wp.impact(FxKind.Singularity));
  add(X, "Ricochet", wp.ricochet);
  add(X, "Round whizzes past", wp.whiz);
  add(X, "Chain Hook lands", wp.hookHit);
  for (let i = 1; i <= 3; i++) add(X, `Repair Orb bounce ${i}`, wp.orbBounce(i));
  add(X, "Barrage missile launch", wp.missileLaunch);
  add(X, "Snare Trap armed", wp.snareArmed);
  add(X, "Gateway portal opens", wp.portalOpen);
  add(X, "Ward up", wp.wardUp);
  add(X, "Ward down", wp.wardDown);
  add(X, "Ward breaks", wp.wardBreak);
  add(X, "Sprint", wp.sprintStart);
  add(X, "Health pack", wp.pickup(false));
  add(X, "Large health pack", wp.pickup(true));
  add(X, "Revive", wp.revive);
  add(X, "Sleep Dart lands", wp.lullaby);
  add(X, "Surge lands", wp.surged);

  for (const h of HERO_LIST) if (h.reloadTime > 0) add("Handling", `${h.name} · reload`, wp.reload(h.id, h.reloadTime));
  add("Handling", "Melee", wp.melee);
  add("Handling", "Dry fire", wp.dryFire);
  add("Handling", "Scope in", wp.scope(true));
  add("Handling", "Scope out", wp.scope(false));

  const F = "Feedback";
  add(F, "Hit", fb.hitTick(0), "feedback");
  add(F, "Hit · combo 4", fb.hitTick(4), "feedback");
  add(F, "Hit · combo 8", fb.hitTick(8), "feedback");
  add(F, "Critical hit", fb.critTick, "feedback");
  for (let i = 1; i <= 6; i++) add(F, `KO · streak ${i}`, fb.ko(i), "feedback");
  add(F, "Assist", fb.assist, "feedback");
  for (let i = 0; i < 5; i++) add(F, `Heal tick ${i + 1}`, fb.healTick(i), "feedback");
  add(F, "Healed by an ally", fb.healed, "feedback");
  for (const layer of ["shield", "armor", "health"] as const) {
    add(F, `Hurt · ${layer}`, fb.hurt(layer, 0.25), "feedback");
    add(F, `Hurt · ${layer} (heavy)`, fb.hurt(layer, 0.9), "feedback");
  }
  for (const h of HERO_LIST) add(F, `Ultimate ready · ${h.name}`, fb.ultReady(h.id), "feedback");
  add(F, "Death", fb.death, "feedback");
  add(F, "Respawn", fb.respawn, "feedback");
  for (const s of ["stun", "sleep", "antiHeal", "boost", "linked"] as const) add(F, `Status · ${s}`, fb.status(s), "feedback");

  const M = "Movement";
  for (const w of ["light", "mid", "heavy"] as const) {
    add(M, `Footstep · ${w}`, fb.footstep(w, false, 0));
    add(M, `Footstep · ${w} (enemy)`, fb.footstep(w, true, 1));
  }
  for (const w of ["light", "mid", "heavy"] as const) add(M, `Jump · ${w}`, fb.jump(w));
  add(M, "Land · light, soft", fb.land("light", 4));
  add(M, "Land · mid", fb.land("mid", 9));
  add(M, "Land · heavy, hard", fb.land("heavy", 16));

  for (let n = 5; n >= 1; n--) add("Match", `Countdown ${n}`, mt.countdown(n), "music");
  add("Match", "Objective unlocked", mt.unlock, "music");
  add("Match", "Round intro", mt.roundIntro, "music");
  add("Match", "We captured it", mt.captured, "music");
  add("Match", "They captured it", mt.pointLost, "music");
  add("Match", "Round won", mt.roundWon, "music");
  add("Match", "Round lost", mt.roundLost, "music");
  add("Match", "Victory", mt.victory, "music");
  add("Match", "Defeat", mt.defeat, "music");

  const I = "Interface";
  add(I, "Hover", uiq.hover, "ui");
  add(I, "Press", uiq.press, "ui");
  add(I, "Back", uiq.back, "ui");
  add(I, "Toggle on", uiq.toggle(true), "ui");
  add(I, "Toggle off", uiq.toggle(false), "ui");
  add(I, "Panel open", uiq.panel(true), "ui");
  add(I, "Panel close", uiq.panel(false), "ui");
  add(I, "Toast", uiq.toast(false), "ui");
  add(I, "Toast · error", uiq.toast(true), "ui");
  add(I, "Chat message", uiq.chat, "ui");

  add("Ambience", "Distant machinery", loops.machinery, "amb");
  return out;
}

export function loopList(): CatalogLoop[] {
  const L = (group: string, name: string, build: (o: Out) => LoopVoice, bus: BusName = "sfx", extra: Partial<CatalogLoop> = {}): CatalogLoop => ({ id: `${group}/${name}`, group, name, build, bus, spatial: bus === "sfx" || bus === "amb", ...extra });
  return [
    L("Hero", "Ember · Hover Jets", loops.jets),
    L("Hero", "Anvil · Ward", loops.ward, "sfx", { value: 1, control: "Ward health" }),
    L("Hero", "Anvil · Levitation", loops.levitate),
    L("Hero", "Tether · Take a Breather", loops.breather),
    L("Hero", "Tether · Whole Hog motor", loops.motor),
    L("Hero", "Ember · Barrage", loops.barrage),
    L("Hero", "Volt · Tactical Visor", loops.visor, "feedback"),
    L("Hero", "Pocket Raccoon · Wall Scamper", loops.climb),
    L("Zones", "Biotic Field", loops.bioticField),
    L("Zones", "Gateway portal", loops.portal),
    L("Zones", "Rebirth Beacon", loops.beacon),
    L("Zones", "Amplifier", loops.amplifier),
    L("Projectiles", "Rocket in flight", loops.flight("rocket")),
    L("Projectiles", "Missile in flight", loops.flight("missile")),
    L("Projectiles", "Orb in flight", loops.flight("orb")),
    L("Projectiles", "Chain Hook in flight", loops.flight("hook")),
    L("Objective", "Capture · us", loops.capture(true), "music", { value: 0.3, control: "Capture progress" }),
    L("Objective", "Capture · them", loops.capture(false), "music", { value: 0.3, control: "Capture progress" }),
    L("Objective", "Contested", loops.contested, "music"),
    L("Objective", "Objective hum", loops.pointHum, "sfx", { value: 0, control: "Nobody · us · them" }),
    L("Objective", "Payload", loops.payload, "sfx", { value: 0.66, control: "Push speed" }),
    L("Objective", "Overtime", loops.overtime, "music", { value: 0.8, control: "Heat" }),
    L("Player", "Heartbeat", loops.heartbeat, "feedback", { value: 0.4, control: "Urgency" }),
    L("Player", "Foundry room tone", loops.roomTone, "amb"),
  ];
}

export const announcerLines = () => (Object.entries(ANNOUNCER.lines) as [LineId, string][]).map(([id, text]) => ({ id, text, url: `/assets/audio/vo/${id}.mp3` }));

export const heroVoiceLines = () =>
  HERO_VOICE_LIST.flatMap((script) => {
    const hero = HEROES[script.hero];
    return (Object.entries(script.lines) as [VoiceTrigger, NonNullable<(typeof script.lines)[VoiceTrigger]>][])
      .flatMap(([trigger, lines]) => lines.map((line) => ({ hero: hero.name, slug: hero.slug, trigger, id: line.id, text: line.text, url: `/assets/audio/heroes/${hero.slug}/${line.id}.mp3` })));
  });

export const heroName = (id: number) => HEROES[id as keyof typeof HEROES]?.name ?? "?";

/** Everything that fires often enough to be worth baking before the first fight. */
export function hotCues(): Cue[] {
  const weights = ["light", "mid", "heavy"] as const;
  return [
    ...HERO_LIST.map((h) => wp.fire(h.id)),
    ...HERO_LIST.map((h) => CLIENT_HEROES[h.id].audio.fire({ alt: true })).filter((c, i) => c.key !== CLIENT_HEROES[HERO_LIST[i]!.id].audio.fire().key).map((c) => c.cue),
    fb.hitTick(0),
    fb.critTick,
    fb.healTick(0),
    ...weights.flatMap((w) => [fb.footstep(w, false, 0), fb.footstep(w, true, 0)]),
    ...(["shield", "armor", "health"] as const).flatMap((l) => [fb.hurt(l, 0.3), fb.hurt(l, 0.9)]),
    wp.ricochet,
    wp.whiz,
    wp.explosion(FxKind.Rocket, 2.8),
    wp.explosion(FxKind.Rocket, 2),
    wp.explosion(FxKind.Missile, 1.8),
    wp.missileLaunch,
    wp.explosion(FxKind.Concussion, 4),
    wp.explosion(FxKind.Spore, 4),
    wp.explosion(FxKind.Singularity, 6),
    wp.explosion(FxKind.Singularity, 12),
    ...[FxKind.Pellet, FxKind.Spore, FxKind.Hook, FxKind.Singularity].map((k) => wp.impact(k)),
    ...weights.flatMap((w) => [fb.jump(w), fb.land(w, 5), fb.land(w, 9), fb.land(w, 15)]),
    ...HERO_LIST.filter((h) => h.reloadTime > 0).map((h) => wp.reload(h.id, h.reloadTime)),
    wp.melee,
    wp.dryFire,
    wp.hookHit,
    wp.snareArmed,
    wp.orbBounce(0),
  ];
}
